/**
 * @fileoverview Defines the `HowDoesRefMcp` component, a blog post explaining how Ref uses Model Context Protocol (MCP) for documentation search. It discusses Ref's approach using MCP features like `Tools` and `Sessions`, and utilizes components like `BlogImage` and `Navbar`. The post highlights the benefits of using `ref_search_documentation` and `ref_read_url`.
 */

import 'lite-youtube-embed/src/lite-yt-embed.css'
import 'lite-youtube-embed/src/lite-yt-embed.js'
import BlogImage from '../../components/BlogImage'
import Contact from '../../components/Contact'
import { Navbar } from '../../components/Navbar'
import '../../styles/pages/BlogPost.css'

export default function HowDoesRefMcp() {
  return (
    <div className="home-container">
      <Navbar pageName="Blog" pageNameLink="/blog" />
      <div className="blog-post-container">
        <h1>How Ref uses MCP to build the best possible documentation search</h1>
        <h4>And why we're excited about it.</h4>

        <div className="blog-content">
          <p>
            Model Context Protocol (MCP) allows engineers to decompose complex AI systems into
            sub-problems for engineers to solve in isolation. Those solutions can then be combined
            to build even better systems. For example, coding agents need to read documentation and
            Ref is focused on providing the best possible solution to that sub-problem. This post
            will explore how Ref takes advantage of the MCP spec to achieve that goal.
          </p>

          <BlogImage
            src="/images/ref-mcp/matt.png"
            alt="Enthusiastically talking about MCP"
            caption="What I look like when I get to talk about MCP (according to my wife)"
          />

          <h2>Background</h2>
          <p>
            Before we jump into how Ref uses MCP, let's cover a little background on why you might
            use an MCP server like Ref and what Ref actually does. There's a lot more that could be
            said on each of these topics but here's enough to get started.
          </p>

          <h3>Why do coding agents need documentation search?</h3>
          <p>
            LLMs are fantastic at one-shot code generation for common cases and common libraries
            (eg. Tailwind) but with more obscure libraries they tend to hallucinate. And this
            problem is magnified when it comes to internal libraries that are not in the model's
            training corpus. The way around this is to provide a little context like a snippet of
            documentation to guide the model. These are just facts that exist in the world and the
            engineer should not need to manually gather them, the agent should be able to quickly
            search and find what it needs.
          </p>

          <p>
            On a more philosophic level, for a coding agent to work autonomously, it needs to be
            able to access all the information a human engineer would for the same task. Every time
            the human engineer needs to go look up a fact about some system or library and pass it
            to the agent, that breaks autonomy.{' '}
            <a href="https://reactiverobot.com/writing/autonomy.html">Check out this post</a> for
            more on that.
          </p>

          <h3>Why does choosing the right tokens matter?</h3>
          <p>
            Okay so we know we need to provide some documentation context. Should we just dump a
            bunch of documentation into context to start and call it a day?
          </p>

          <p>It turns out, no. We should not just dump a bunch of stuff in context window.</p>

          <p>
            There are a couple reasons why that's not desirable. First, as I'm sure everyone is
            aware, tokens cost money. And second, even the cutting edge long-context window models{' '}
            <a href="https://research.trychroma.com/context-rot">
              get a little dumber the more tokens you throw at them
            </a>
            . This is why it's often better to wipe the slate clean and start fresh rather than
            trying to wrangle Claude in a super long thread.
          </p>

          <h3>At a high level, how does Ref work?</h3>

          <p>
            Ref is an MCP server that provides coding agents a way to search and read documentation.
            Ref crawls public and private documentation from the web and GitHub, parses and chunks
            it into meaningful snippets and indexes them. Coding agents like Cursor and Claude Code
            then use the Ref MCP server to search for relevant documentation and read it.
          </p>

          <p>
            A more detailed technical write up is in the works but if you're interested in how we
            evaluate this search system{' '}
            <a href="/blog/how-make-search-good">you can read more here.</a>
          </p>

          <BlogImage
            src="/images/ref-mcp/ref-high-level.png"
            alt="Real search"
            caption="A very high level overview of how Ref works. The goal is to provide exactly the tokens the agent needs."
          />

          <h2>Ref & MCP</h2>

          <p>
            The MCP spec has a ton of nifty capabilities that the team has thought carefully about.
            If you haven't checked it out, I recommend browsing{' '}
            <a href="https://modelcontextprotocol.io/introduction" target="_blank">
              the MCP docs
            </a>
            , if only to get a sense of the possibilities. In this section, I'll cover what parts of
            the MCP spec Ref takes advantage of and why.
          </p>

          <h3>Tools</h3>

          <p>
            Tools are the bread and butter of MCP that every client supports. Ref provides two
            tools: <code>ref_search_documentation(query)</code> and <code>ref_read_url(url)</code>.
            These do exactly what you'd expect: <code>ref_search_documentation</code> searches for
            documentation and returns a list of URLs with a little additional information about the
            content. The agent can then use <code>ref_read_url</code> to read relevant results. We
            also enable <code>ref_read_url</code> to scrape any URL on the fly since documentation
            often contains links out to other relevant sources.
          </p>

          <p>
            Ref's tools are also compatible with OpenAI deep research. It requires tool names{' '}
            <code>search</code> and <code>fetch</code> so we use the MCP client id to change tool
            names as needed. We don't use these names generally because they're so generic and users
            have reported confusion if Ref is actually being used or not.
          </p>

          <h3>Prompts</h3>

          <p>
            One of the challenges with MCP is getting the agent to use the tools when needed. It
            often requires explicitly prompting "... and search the documentation with ref". MCP
            prompts are a great solution to that so that clients can autocomplete the needed
            prompting. Ref provides two prompts: <code>search_docs</code> for a search of public
            documentation and <code>my_docs</code> to guide the agent to search private
            documentation.
          </p>

          <BlogImage
            src="/images/ref-mcp/tool-calls.png"
            alt="Tool call"
            caption="What it looks like to use Ref in Claude Code."
          />

          <h3>Sessions</h3>

          <p>
            Sessions allow an MCP server to maintain state between tool calls and this is where
            things start to get really interesting. Ref takes advantage of this in a few pretty neat
            ways.
          </p>

          <p>
            <strong>No repeating results.</strong> Coding and research agents do iterative searches
            where they refine a query and search again. Ref will track what results it's returned
            and not repeat itself. This has two effects: (1) not wasting tokens on repeated
            information and (2) letting agents iterate in fewer loops by not having to page through
            repeated results.
          </p>

          <p>
            <strong>Caching search results for quicker reads.</strong> Since Ref knows which pages
            it has returned for search, it can pre-cache results so page reads are super fast. When
            you're using a foreground agent the search and fetch research loop can be annoyingly
            slow but Ref's cached read is fast enough it just feels like the agent doesn't have to
            stop thinking.
          </p>

          <p>
            <strong>Only reading relevant tokens from large pages.</strong> The reason isolated web
            fetch tools tend to hog tokens is that converting a URL to markdown often results in a
            ton of erroneous tokens like page headers and footers. Ref takes advantage of knowing
            your query history to only read what's relevant for the agent's current task. If a
            result would take more than 5000 tokens, Ref will read the first 5000 tokens and then do
            a similarity search to find the most relevant parts of the page.
          </p>

          <BlogImage
            src="/images/ref-mcp/real-session.png"
            alt="Real session"
            caption="A real session where the agent iteratively searches and reads from several different documentation sources."
          />

          <h2>Looking Forward</h2>

          <p>
            The MCP spec offers many more capabilities we're excited to explore. Here's a taste of
            what's on the menu.
          </p>

          <h3>Resources</h3>
          <p>
            Resources are a way for MCP servers to provide application-specific information to
            clients. This means Ref could provide a list of documentation sources and the IDE could
            use that to autocomplete and make it super easy to prompt what to search.
          </p>

          <p>
            One feature we'd like to add to Ref is defining a stack for your team including specific
            versions of libraries so your code generation is always grounded in exactly your stack.
            You might have a web stack, an infra stack etc and we could use resources to expose that
            to the client.
          </p>

          <h3>Elicitation</h3>
          <p>
            Elicitations allow MCP server to ask the client for more information. This could be
            really great for query rewriting. For example, the most common cause of searches failing
            to find the right result with Ref is missing an important detail like which programming
            language we want. Elicitations could allow Ref to ask which language ahead of time if
            it's missing or even wait to ask until we have multiple search results and need to
            choose the correct one.
          </p>

          <h3>OAuth</h3>
          <p>
            MCP authentication is something that has been refined a lot in the last few months. We'd
            love to add OAuth support to Ref to make it even easier to set up.
          </p>

          <h2>A request to client developers</h2>
          <p>
            Many clients only implement support for some features of the MCP spec which means many
            of the possible cool features are dead on arrival. There's a lot there and building a
            great plugin experience on top of a complex product like an IDE is a challenging problem
            so I get it. That just means more kudos to folks like the VSCode team for implementing{' '}
            <a
              href="https://code.visualstudio.com/blogs/2025/06/12/full-mcp-spec-support"
              target="_blank"
            >
              full MCP spec support
            </a>
            . Cursor has had my heart for the last year but full MCP support has me spending time in
            VSCode again. I encourage anyone developing an MCP client to follow the VSCode team's
            lead!
          </p>
        </div>
      </div>
      <Contact />
    </div>
  )
}
